import { Box, Button, CircularProgress, Fab, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PageHeader from "../../materio/@core/components/page-header";
import { client } from "../../utils/axios-client";
import SimpleDialog from "../common/SimpleDialog";
import { UpdateRequest, ValidationResult } from "./GlobalUpdateTypes";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import CheckIcon from '@mui/icons-material/Check';
import { green, red } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { ApiResponseType } from "../../types";
type Props = {
    open: boolean,
    onClose: (updated: boolean) => void,
    leadIds: string[],
    updateRequest: UpdateRequest
}

const GlobalUpdateProgressDialog = ({ open, onClose, leadIds, updateRequest }: Props) => {
    const [hasUpdated, setHasUpdated] = useState(false);
    const [validating, setValidating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [updateFailed, setUpdateFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    useEffect(() => {
        if (open) {
            validateUpdate();
        }
    }, [open])

    const runUpdate = async () => {
        setUpdating(true);

        var response = await client.post<ApiResponseType>('/api/leads/datamanagement/global-updates', { ...updateRequest, leadIds: leadIds });

        setUpdateFailed(!response.data.succeeded);

        if (!response.data.succeeded && response.data.errors.length > 0) {
            setErrorMessage(response.data.errors[0].description);
        } else {
            setErrorMessage("An unknown error occured");
        }

        setUpdating(false);
        setHasUpdated(true);
    }



    const validateUpdate = async () => {
        setValidating(true);
        setErrorMessage('');
        var response = await client.post<ValidationResult>('/api/leads/datamanagement/global-updates/validate', { ...updateRequest, leadIds: leadIds });

        setValidationResult(response.data);
        setValidating(false);

        if (response.data.success) {
            runUpdate();
        }
    }

    const buttonSx = {
        ...(!validating && validationResult && validationResult.success && {
            bgcolor: green[500],
            '&:hover': {
                bgcolor: green[700],
            },
        }),
        ...(!validating && validationResult && !validationResult.success && {
            bgcolor: red[500],
            '&:hover': {
                bgcolor: red[700],
            },
        }),
        ...(validating && {
            bgcolor: 'gray',
            '&:hover': {
                bgcolor: 'gray',
            },
        }),
    };

    const buttonSx2 = {
        ...(!updating && !updateFailed && hasUpdated && {
            bgcolor: green[500],
            '&:hover': {
                bgcolor: green[700],
            },
        }),
        ...(!updating && updateFailed && hasUpdated && {
            bgcolor: red[500],
            '&:hover': {
                bgcolor: red[700],
            },
        }),
        ...(!hasUpdated && {
            bgcolor: 'gray',
            '&:hover': {
                bgcolor: 'gray',
            },
        }),
    };

    return <SimpleDialog open={open} onClose={() => onClose(hasUpdated && !updateFailed)} size={validationResult && !validationResult.success ? 'lg' : 'sm'} >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Box>
                <Typography variant='h5'>Mass Update</Typography>
            </Box>

            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                <ListItem>
                    <ListItemText primary={validating ? "Validating Update" : validationResult && validationResult.success ? "Validation Successful" : "Validation Failed"} />

                    <Box sx={{ m: 1, position: 'relative' }}>
                        <Fab
                            disabled={validating}
                            size="small"
                            aria-label="save"
                            sx={buttonSx}
                        >
                            {!validating ? validationResult && validationResult.success ? <CheckIcon /> : <CloseIcon /> : <CheckIcon />}
                        </Fab>
                        {validating && (
                            <CircularProgress
                                size={48}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: -5,
                                    left: -4,
                                    zIndex: 1,
                                }}
                            />
                        )}
                    </Box>
                </ListItem>
                <ListItem>
                    <ListItemText disableTypography primary={!hasUpdated && !updating ? "Preparing to Update" : updating ? "Updating Leads" : !updateFailed && hasUpdated ? `Update Successful - ${leadIds.length} leads updated` : `Update Failed - ${errorMessage}`} />

                    <Box sx={{ m: 1, position: 'relative' }}>
                        <Fab
                            disabled={validating || updating}
                            size="small"
                            aria-label="save"
                            sx={buttonSx2}
                        >
                            {!updating ? !updateFailed ? <CheckIcon /> : <CloseIcon /> : <CheckIcon />}
                        </Fab>
                        {updating && (
                            <CircularProgress
                                size={48}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: -5,
                                    left: -4,
                                    zIndex: 1,
                                }}
                            />
                        )}
                    </Box>
                </ListItem>
            </List>



            {validationResult && !validationResult.success && <Box>
                <Typography variant="body2" color="error"> The following errors occured while validating the global update.</Typography>
                <TableContainer sx={{ width: '100%', maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Lead Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Reason</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {validationResult.errors.map(item => <TableRow>
                                <TableCell>{item.leadName}</TableCell>
                                <TableCell>{item.leadEmail}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" size="small" onClick={() => onClose(hasUpdated && !updateFailed)} disabled={validating || updating}>Close</Button>
            </Box>
        </Box>
    </SimpleDialog>
}

export default GlobalUpdateProgressDialog;